/* eslint-disable parog-web/no-glint-expect-error-directive */
import type { TemplateOnlyComponent } from '@ember/component/template-only';
import dayjs from 'dayjs';
import {
  validateDate,
  validateNumber
} from 'ember-changeset-validations/validators';
import type { Payload } from 'parog-web/components/forc/abstract/journal-event-based-component';
import BaseMedicalHistoryFormComponent from 'parog-web/components/medical-history/abstract/base-edition-form';
import MedicalHistoryBaseEditionForm from './base-edition-form';
import PneumoGenericDiagnostic from './pneumo/pneumo-generic/diagnostic-fields';
import PneumoTransplantDiagnostic from './pneumo/transplant/diagnostic-fields';
import PneumoTransplantPathology from './pneumo/transplant/pathology-fields';

const PathologyFields = {
  transplant: PneumoTransplantPathology
};

const DiagnosticFields = {
  'pneumo-generic': PneumoGenericDiagnostic,
  transplant: PneumoTransplantDiagnostic
};

export default class MedicalHistoryFormComponent extends BaseMedicalHistoryFormComponent {
  override journalEventProperties = [
    ...this.sharedProperties,
    ...this.renalPathologyProperties,
    ...this.pulmonaryPathologyProperties
  ];

  get diagnostic(): TemplateOnlyComponent {
    return DiagnosticFields[this.patho ?? ''];
  }

  get pathology(): TemplateOnlyComponent {
    return PathologyFields[this.patho ?? ''];
  }

  get pulmonaryPathologyProperties(): string[] {
    return [
      'transplantedOrgan',
      'underlyingDisease',
      'transplantDate',
      'transplantRank',
      'cytomegalovirusStatus'
    ];
  }

  get renalPathologyProperties(): string[] {
    return [
      'transplantDate',
      'transplantRank',
      'nephropatie',
      'donorType',
      'immunologicalRisk',
      'incompatibleABO',
      'donorCreatinine',
      'abmClassification',
      'creatinineThreeMonthsAfterTransplant',
      'rejectsFirstYear',
      'mrcStage',
      'mrcPreexistingTransplant',
      'transplantedOrgan',
      'cytomegalovirusStatus'
    ];
  }

  override get validations(): Record<string, unknown> {
    return {
      creatinineThreeMonthsAfterTransplant: validateNumber({
        gte: 20,
        lte: 2000,
        allowBlank: true,
        description: 'La quantité de créatinine après 3 mois'
      }),
      transplantDate: [
        validateDate({
          allowBlank: true,
          message:
            'Cette donnée ne peut être déclarée avant 1900. Veuillez modifier la date',
          onOrAfter: new Date('01/01/1900')
        }),
        validateDate({
          allowBlank: true,
          message:
            'Cette donnée ne peut être déclarée dans le futur. Veuillez modifier la date',
          onOrBefore: dayjs().endOf('day').toDate()
        })
      ]
    };
  }

  override computePayload(): Payload {
    const payload = super.computePayload();

    if (!this.args.patient.isMrc) {
      delete payload.mrcStage;
      delete payload.mrcPreexistingTransplant;
    }
    if (!this.args.patient.isTransplanted) {
      delete payload.transplantedOrgan;
    }

    return payload;
  }

  override retrieveModel(): Record<string, unknown> {
    const model = super.retrieveModel();
    if (!this.isLoadedFromJe) {
      model.transplantedOrgan = null;
    }

    return model;
  }

  <template>
    <MedicalHistoryBaseEditionForm
      {{! @glint-expect-error }}
      @forcComponent={{this}}
      @patient={{@patient}}
      as |mh|
    >
      <mh.FurSection />
      <mh.FurDetailsSection @diagnostic={{this.diagnostic}} />
      <mh.ComorbiditiesAllergiesSection />
      <mh.PathologySection @pathology={{this.pathology}} />
      <mh.VaccinesSection />
      <mh.CommentsSection />
    </MedicalHistoryBaseEditionForm>
  </template>
}
